/**
 * Друкована форма «Акт виконаних робіт (наданих послуг)» для акта
 * виробничих послуг (ч.701). Канон друку ч.669/677: PrintActions
 * (друк/«підписати і зберегти» з печатками), Times, .xx-table.
 * Рядки: номенклатура-послуга + «Зміст послуги» (0471 description),
 * суми/ПДВ по рядках; договір у шапці; підсумок словами; підписи сторін.
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productionServiceActs } from '@/api/productionServiceActs';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import PrintLines1c, { type PrintLine } from '@/shared/components/PrintLines1c';
import type { VatMode } from '@/shared/utils/vatFlags';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';


const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}

export default function ProductionServiceActPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['production-service-act', id],
    queryFn: () => productionServiceActs.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', docQ.data?.organization_id, docQ.data?.customer_contractor_id],
    queryFn: () => refs.contracts({
      organization_id: docQ.data!.organization_id,
      contractor_id: docQ.data!.customer_contractor_id,
    }),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const nomMap = useMemo(() => {
    const m = new Map<string, { code: string | null; name: string }>();
    nomQ.data?.items.forEach((n) => m.set(n.id, { code: n.code, name: n.name }));
    return m;
  }, [nomQ.data]);

  if (!docQ.data || !orgsQ.data || !contractorsQ.data || !nomQ.data) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  const d = docQ.data;
  const org = orgsQ.data.items.find((o) => o.id === d.organization_id);
  const customer = contractorsQ.data.items.find((c) => c.id === d.customer_contractor_id);
  const contract = (contractsQ.data ?? []).find((c) => c.id === d.contract_id);

  if (!org || !customer) {
    return <div className="card"><p>Не знайдено організацію або замовника.</p></div>;
  }

  const items = d.items;
  // ч.1374: таблиця, підсумки і прописом — за зразком 1С; акт виробничих послуг власних
  // ознак ПДВ не має — за організацією, а ПДВ у рядках (ч.1381: статус міг змінитись) — з ПДВ.
  const hasVat = items.some((it) => Number(it.vat_amount ?? 0) > 0);
  const mode: VatMode = org.is_vat_payer || hasVat ? 'excluded' : 'none';
  const net = items.reduce((a, it) => a + Number(it.amount), 0);
  const vat = mode === 'none' ? 0 : items.reduce((a, it) => a + Number(it.vat_amount ?? 0), 0);
  const lines: PrintLine[] = items.map((it) => {
    const nom = nomMap.get(it.nomenclature_id);
    return {
      name: `${nom?.name ?? '—'}${it.description ? ` — ${it.description}` : nom?.code ? ` (${nom.code})` : ''}`,
      qty: Number(it.quantity ?? '1'),
      uom: 'посл.',
      price: Number(it.price),
      amount: Number(it.amount),
    };
  });

  return (
    <div className="print-page psa-print">
      <style>{`
        .psa-print {
          background: white; color: black; max-width: 900px; margin: 0 auto;
          padding: 1.5rem;
          font-family: 'Times New Roman', serif; font-size: 10.5pt; line-height: 1.3;
        }
        .psa-print h1 { font-size: 13pt; text-align: center; margin: 0; }
        .psa-print .center { text-align: center; }
        .psa-print .right { text-align: right; }
        .psa-print .small { font-size: 9pt; color: #555; }
        .psa-print .party { margin: 0.5rem 0; }
        .psa-print .party .label { font-size: 9pt; color: #555; }
        .psa-print table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 0.75rem; }
        .psa-print th, .psa-print td { border: 1px solid black; padding: 4px 6px; vertical-align: top; }
        .psa-print th { background: #f5f5f5; font-weight: 600; text-align: center; text-transform: none; letter-spacing: 0; font-size: 8pt; }
        .psa-print td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .psa-print .totals-line { margin-top: 0.5rem; }
        .psa-print .signs { margin-top: 1.5rem; display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
        .psa-print .sign { border-bottom: 1px solid black; min-height: 22px; }
        .psa-print .sign-label { font-size: 9pt; color: #555; margin-top: 4px; }
        @media print {
          .psa-print { padding: 0; max-width: none; }
          body { background: white !important; }
        }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.production_service_acts',
        documentId: d.id,
        filename: `Акт послуг ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ marginBottom: '0.75rem' }}>
        <h1>Акт виконаних робіт (наданих послуг) № {d.document_number}</h1>
        <p className="center" style={{ margin: '0.25rem 0' }}>від {formatDateUk(d.document_date)}</p>
      </div>

      <div className="party">
        <span className="label">Виконавець: </span>
        <strong>{org.full_name || org.name}</strong>
        {partyCodesText(org) && <span>, {partyCodesText(org)}</span>}
      </div>

      <div className="party">
        <span className="label">Замовник: </span>
        <strong>{customer.full_name || customer.name}</strong>
        {partyCodesText(customer) && <span>, {partyCodesText(customer)}</span>}
      </div>

      {contract && (
        <div className="party">
          <span className="label">Договір: </span>
          {contractLabel(contract)}
        </div>
      )}

      <p style={{ margin: '0.5rem 0' }}>
        Ми, що нижче підписалися, представник Виконавця з одного боку і
        представник Замовника з іншого боку, склали цей акт про те, що
        Виконавцем були виконані такі роботи (надані такі послуги):
      </p>

      <PrintLines1c mode={mode} lines={lines} net={net} vat={vat}
        nameHeader="Найменування робіт (послуг)" countLabel="Всього надано послуг" />

      <p style={{ margin: '0.75rem 0 0' }}>
        Сторони претензій одна до одної не мають. Роботи (послуги) виконані
        у повному обсязі та належної якості.
      </p>
      {d.comment && <p className="small" style={{ margin: '0.5rem 0 0' }}>Коментар: {d.comment}</p>}

      <div className="signs">
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Виконавець (підпис, П.І.Б.)</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Замовник (підпис, П.І.Б.)</div>
        </div>
      </div>

      <p className="small" style={{ marginTop: '1rem' }}>
        М.П. Виконавця &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        М.П. Замовника
      </p>
    </div>
  );
}
